import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { ResultImportResumeModel } from "../../../model/import-resume/result-import-resume.model";
import { InputResumeBuilderModel, InputResumeObject } from "../../../model/resume-builder/input-resume-builder.model";
import moment from "moment";
import "./prefill-resume.modal.scss";

interface PrefillResumeModalProps {
    open: boolean;
    handleClose: () => void;
    parsedData: ResultImportResumeModel;
    onPrefill: (data: Partial<InputResumeBuilderModel>) => void;
    translate?: any;
}

const PrefillResumeModal = ({ open, handleClose, parsedData, onPrefill, translate }: PrefillResumeModalProps) => {
    const [selectedFields, setSelectedFields] = useState<{
        name: boolean;
        contact: boolean;
        position: boolean;
        experience: boolean;
        education: boolean;
        expertise: boolean;
    }>({
        name: true,
        contact: true,
        experience: true,
        education: true,
        expertise: true,
        position: true,
    });

    // Convert parsed data to resume builder format
    const convertToResumeFormat = () => {
        const result: Partial<InputResumeBuilderModel> = {};

        // Name
        if (selectedFields.name && parsedData.firstName && parsedData.lastName) {
            result.name = {
                value: `${parsedData.firstName} ${parsedData.lastName}`,
                isEdit: false,
                isShow: true
            } as InputResumeObject;
        }

        // Contact
        if (selectedFields.contact) {
            let contactHtml = "";
            if (parsedData.address) {
                contactHtml += `<span><strong>Address:</strong> ${parsedData.address}</span><br>`;
            }
            if (parsedData.phone) {
                const phoneDisplay = Array.isArray(parsedData.phone) 
                    ? parsedData.phone.join(", ") 
                    : parsedData.phone;
                contactHtml += `<span><strong>Tel:</strong> ${phoneDisplay}</span><br>`;
            }
            if (parsedData.email) {
                contactHtml += `<span><strong>Email:</strong> ${parsedData.email}</span><br>`;
            }
            if (parsedData.lineId) {
                contactHtml += `<span><strong>LINE ID:</strong> ${parsedData.lineId}</span><br>`;
            }
            if (contactHtml) {
                result.contact = {
                    value: contactHtml,
                    isEdit: false,
                    isShow: true
                } as InputResumeObject;
            }
        }

        // Position (last job title)
        if (selectedFields.position && parsedData.experiences && parsedData.experiences.length > 0) {
            const lastExp = parsedData.experiences[parsedData.experiences.length - 1];
            if (lastExp.jobTitle) {
                result.position = {
                    value: lastExp.jobTitle,
                    isEdit: false,
                    isShow: true
                } as InputResumeObject;
            }
        }

        // Experience
        if (selectedFields.experience && parsedData.experiences && parsedData.experiences.length > 0) {
            let expHtml = "<ul>";
            parsedData.experiences.forEach((exp) => {
                expHtml += "<li>";
                if (exp.startDate) {
                    try {
                        const startDate = moment(exp.startDate);
                        if (startDate.isValid()) {
                            expHtml += `<span><strong>${startDate.format("MMMM YYYY")}</strong>`;
                        } else {
                            expHtml += `<span><strong>${exp.startDate}</strong>`;
                        }
                    } catch (e) {
                        expHtml += `<span><strong>${exp.startDate}</strong>`;
                    }
                }
                if (exp.stillWorkHere) {
                    expHtml += `<strong> - present</strong></span><br>`;
                } else if (exp.endDate) {
                    try {
                        const endDate = moment(exp.endDate);
                        if (endDate.isValid()) {
                            expHtml += `<strong> - ${endDate.format("MMMM YYYY")}</strong></span><br>`;
                        } else {
                            expHtml += `<strong> - ${exp.endDate}</strong></span><br>`;
                        }
                    } catch (e) {
                        expHtml += `<strong> - ${exp.endDate}</strong></span><br>`;
                    }
                }
                if (exp.jobTitle) {
                    expHtml += `<span>Position: <strong>${exp.jobTitle}</strong></span><br>`;
                }
                if (exp.companyName) {
                    expHtml += `<span>Company: <strong>${exp.companyName}</strong></span><br>`;
                }
                if (exp.responsibilities) {
                    expHtml += `<span>Responsibility: <strong>${exp.responsibilities}</strong></span><br>`;
                }
                expHtml += "</li><br>";
            });
            expHtml += "</ul>";
            result.experience = {
                value: expHtml,
                isEdit: false,
                isShow: true
            } as InputResumeObject;
        }

        // Education
        if (selectedFields.education && parsedData.educations && parsedData.educations.length > 0) {
            let eduHtml = "<ul>";
            parsedData.educations.forEach((edu) => {
                eduHtml += "<li>";
                if (edu.startYear) {
                    eduHtml += `<span><strong>${edu.startYear}</strong>`;
                }
                if (edu.graduationYear) {
                    eduHtml += `<strong> - ${edu.graduationYear}</strong></span><br>`;
                }
                if (edu.educationLevel) {
                    eduHtml += `<span><strong>${edu.educationLevel}</strong></span><br>`;
                }
                if (edu.fieldOfStudy && edu.faculty && edu.institute) {
                    eduHtml += `<span><strong>${edu.fieldOfStudy}, ${edu.faculty} at ${edu.institute}</strong></span><br>`;
                } else if (edu.institute) {
                    eduHtml += `<span><strong>at ${edu.institute}</strong></span><br>`;
                }
                if (edu.gpax) {
                    eduHtml += `<span>GPA: <strong>${edu.gpax}</strong></span><br>`;
                }
                eduHtml += "<br></li>";
            });
            eduHtml += "</ul>";
            result.education = {
                value: eduHtml,
                isEdit: false,
                isShow: true
            } as InputResumeObject;
        }

        // Expertise (Skills)
        if (selectedFields.expertise && parsedData.skills && parsedData.skills.length > 0) {
            let skillHtml = "<ul>";
            parsedData.skills.forEach((skill) => {
                skillHtml += `<li>${skill.skillName}</li>`;
            });
            skillHtml += "</ul>";
            result.expertise = {
                value: skillHtml,
                isEdit: false,
                isShow: true
            } as InputResumeObject;
        }

        return result;
    };

    const handleApply = () => {
        const convertedData = convertToResumeFormat();
        onPrefill(convertedData);
        handleClose();
    };

    const toggleField = (field: keyof typeof selectedFields) => {
        setSelectedFields(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    return (
        <Modal show={open} onHide={handleClose} className="prefill-resume-modal" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>ข้อมูลที่พบใน Resume</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="prefill-resume-content">
                    <p className="prefill-resume-description">
                        เลือกข้อมูลที่ต้องการนำไปใช้ใน Resume Builder:
                    </p>

                    {/* Name */}
                    {parsedData.firstName && parsedData.lastName && (
                        <div className="prefill-resume-field">
                            <Form.Check
                                type="checkbox"
                                checked={selectedFields.name}
                                onChange={() => toggleField("name")}
                                label={
                                    <div>
                                        <strong>ชื่อ:</strong> {parsedData.firstName} {parsedData.lastName}
                                    </div>
                                }
                            />
                        </div>
                    )}

                    {/* Contact */}
                    {(parsedData.email || parsedData.phone || parsedData.address || parsedData.lineId) && (
                        <div className="prefill-resume-field">
                            <Form.Check
                                type="checkbox"
                                checked={selectedFields.contact}
                                onChange={() => toggleField("contact")}
                                label={
                                    <div>
                                        <strong>ข้อมูลติดต่อ:</strong>
                                        <ul className="prefill-resume-field-list">
                                            {parsedData.email && <li>Email: {parsedData.email}</li>}
                                            {parsedData.phone && (
                                                <li>
                                                    Tel: {Array.isArray(parsedData.phone) 
                                                        ? parsedData.phone.join(", ") 
                                                        : parsedData.phone}
                                                </li>
                                            )}
                                            {parsedData.address && <li>Address: {parsedData.address}</li>}
                                            {parsedData.lineId && <li>LINE ID: {parsedData.lineId}</li>}
                                        </ul>
                                    </div>
                                }
                            />
                        </div>
                    )}

                    {/* Position */}
                    {parsedData.experiences && parsedData.experiences.length > 0 && (
                        <div className="prefill-resume-field">
                            <Form.Check
                                type="checkbox"
                                checked={selectedFields.position}
                                onChange={() => toggleField("position")}
                                label={
                                    <div>
                                        <strong>ตำแหน่งงานล่าสุด:</strong> {parsedData.experiences[parsedData.experiences.length - 1].jobTitle}
                                    </div>
                                }
                            />
                        </div>
                    )}

                    {/* Experience */}
                    {parsedData.experiences && parsedData.experiences.length > 0 && (
                        <div className="prefill-resume-field">
                            <Form.Check
                                type="checkbox"
                                checked={selectedFields.experience}
                                onChange={() => toggleField("experience")}
                                label={
                                    <div>
                                        <strong>ประสบการณ์การทำงาน ({parsedData.experiences.length} ตำแหน่ง):</strong>
                                        <ul className="prefill-resume-field-list">
                                            {parsedData.experiences.map((exp, idx) => (
                                                <li key={idx}>
                                                    {exp.jobTitle} - {exp.companyName}
                                                    {exp.startDate && ` (${(() => {
                                                        try {
                                                            const date = moment(exp.startDate);
                                                            return date.isValid() ? date.format("MMM YYYY") : exp.startDate;
                                                        } catch {
                                                            return exp.startDate;
                                                        }
                                                    })()} - ${exp.stillWorkHere ? "Present" : exp.endDate ? (() => {
                                                        try {
                                                            const date = moment(exp.endDate);
                                                            return date.isValid() ? date.format("MMM YYYY") : exp.endDate;
                                                        } catch {
                                                            return exp.endDate;
                                                        }
                                                    })() : ""})`}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                }
                            />
                        </div>
                    )}

                    {/* Education */}
                    {parsedData.educations && parsedData.educations.length > 0 && (
                        <div className="prefill-resume-field">
                            <Form.Check
                                type="checkbox"
                                checked={selectedFields.education}
                                onChange={() => toggleField("education")}
                                label={
                                    <div>
                                        <strong>การศึกษา ({parsedData.educations.length} รายการ):</strong>
                                        <ul className="prefill-resume-field-list">
                                            {parsedData.educations.map((edu, idx) => (
                                                <li key={idx}>
                                                    {edu.institute} - {edu.fieldOfStudy || edu.educationLevel}
                                                    {edu.graduationYear && ` (${edu.graduationYear})`}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                }
                            />
                        </div>
                    )}

                    {/* Skills */}
                    {parsedData.skills && parsedData.skills.length > 0 && (
                        <div className="prefill-resume-field">
                            <Form.Check
                                type="checkbox"
                                checked={selectedFields.expertise}
                                onChange={() => toggleField("expertise")}
                                label={
                                    <div>
                                        <strong>ทักษะ ({parsedData.skills.length} ทักษะ):</strong>
                                        <div className="prefill-resume-skills">
                                            {parsedData.skills.map((skill, idx) => (
                                                <span key={idx} className="prefill-resume-skill-tag">
                                                    {skill.skillName}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    ยกเลิก
                </Button>
                <Button variant="primary" onClick={handleApply}>
                    นำข้อมูลไปใช้
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PrefillResumeModal;

